/**
 * Lucid Charts Export Service
 * 
 * Handles document generation, ZIP packaging, and API integration
 * for exporting Contact Flow diagrams to Lucid Charts.
 */

import JSZip from 'jszip';
import { lucidAuthService } from './lucidAuthService';

class LucidExportService {
  constructor() {
    this.API_ENDPOINT = '/documents';
    this.MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB limit for the .lucid (zip) file
    this.MAX_DOCUMENT_SIZE = 1.5 * 1024 * 1024; // 1.5MB hard limit for document.json (Lucid Standard Import)
  }

  /**
   * Export a single diagram using direct Lucid format data (bypasses React Flow transformation)
   * @param {Object} lucidData - Pre-transformed Lucid diagram data
   * @param {Object} flowMetadata - Flow metadata for organization and upload
   * @param {Boolean} downloadLocalCopy - Whether to download local copy of .lucid file (default: false)
   * @returns {Promise<Object>} Export result with document URL and folder info
   */
  async exportSingleDiagramDirect(lucidData, flowMetadata = {}, downloadLocalCopy = false) {
    try {
      // Validate authentication
      if (!lucidAuthService.isAuthenticated()) {
        throw new Error('Not authenticated with Lucid Charts. Please authenticate first.');
      }

      // Validate input data (direct Lucid format)
      if (!lucidData || typeof lucidData !== 'object') {
        throw new Error('Invalid Lucid data provided');
      }

      // Create folder structure for organization (only if not part of batch export)
      let folderInfo = null;
      if (flowMetadata.folderId) {
        // Folder already provided (batch export) - skip folder creation
        folderInfo = {
          subFolderId: flowMetadata.folderId,
          fullPath: 'Batch Export Folder' // Placeholder since we don't need the path for display
        };
      } else if (flowMetadata.instanceName) {
        // Single export - create folder structure (viewType can be null for no subfolder)
        try {
          folderInfo = await this.createFolderStructure(
            flowMetadata.instanceName,
            flowMetadata.viewType,
            flowMetadata.customDate
          );

          if (folderInfo) {
            // Add folder ID to metadata for upload (use mainFolderId if no subfolder)
            flowMetadata.folderId = folderInfo.subFolderId || folderInfo.mainFolderId;
          }
        } catch (folderError) {
          console.warn('LucidExportService: Folder creation failed, continuing without folders:', folderError.message);
          // Continue export without folders
        }
      }

      // Generate .lucid file directly from the provided Lucid data
      const lucidFile = await this.generateLucidFile(lucidData);

      // Download .lucid file locally if requested
      if (downloadLocalCopy) {
        const safeTitle = (flowMetadata.title || flowMetadata.name || 'Unknown_Flow').replace(/[^a-zA-Z0-9]/g, '_');
        const debugFilename = `DEBUG_${safeTitle}_${Date.now()}.lucid`;
        const debugUrl = URL.createObjectURL(lucidFile);
        const debugLink = document.createElement('a');
        debugLink.href = debugUrl;
        debugLink.download = debugFilename;
        debugLink.style.display = 'none';
        document.body.appendChild(debugLink);
        debugLink.click();
        document.body.removeChild(debugLink);
        URL.revokeObjectURL(debugUrl);
        console.log(`Downloaded ${debugFilename} for local inspection`);
      }

      // Upload to Lucid Charts
      const result = await this.uploadToLucid(lucidFile, flowMetadata);

      return {
        success: true,
        documentId: result.id,
        documentUrl: result.url,
        documentName: result.title,
        folderInfo: folderInfo,
        stats: {
          nodesCount: lucidData.pages?.[0]?.objects?.filter(obj => obj.Class === 'Block')?.length || 0,
          linesCount: lucidData.pages?.[0]?.objects?.filter(obj => obj.Class === 'Line')?.length || 0,
          directTransformation: true
        }
      };

    } catch (error) {
      console.error('Single diagram direct export failed:', error);
      throw new Error(`Direct export failed: ${error.message}`);
    }
  }

  /**
   * Export multiple diagrams as a single multi-page Lucid document (or split into multiple documents if size exceeds the 1.5MB document.json limit)
   * This is the BULK export method that combines all contact flows into one document with multiple pages.
   * @param {Array} diagramsData - Array of {lucidData, metadata} objects (each lucidData has 1 page)
   * @param {Object} options - Export options (downloadLocalCopy, onProgress, onError callbacks)
   * @returns {Promise<Object>} Export results with document URLs and split information
   */
  async exportMultipleDiagramsAsMultiPageDocument(diagramsData, options = {}) {
    const {
      downloadLocalCopy = false, // Whether to download .lucid file(s) locally
      onProgress = () => {},
      onError = () => {}
    } = options;

    const results = {
      successful: [],
      failed: [],
      total: diagramsData.length,
      folderInfo: null,
      documentCount: 0,
      splitOccurred: false
    };

    try {
      // Validate authentication
      if (!lucidAuthService.isAuthenticated()) {
        throw new Error('Not authenticated with Lucid Charts. Please authenticate first.');
      }

      if (!Array.isArray(diagramsData) || diagramsData.length === 0) {
        throw new Error('No diagrams provided for export');
      }

      console.log(`Starting multi-page document export for ${diagramsData.length} diagrams`);

      // Create shared folder structure (main folder only, no subfolder for multi-page docs)
      const firstDiagram = diagramsData[0];
      if (firstDiagram.metadata?.instanceName) {
        try {
          // Create just the main folder (no subfolder for multi-page documents)
          const { lucidFolderService } = await import('./lucidFolderService');
          const mainFolder = await lucidFolderService.createMainFolder(
            firstDiagram.metadata.instanceName,
            firstDiagram.metadata.customDate
          );

          if (mainFolder) {
            results.folderInfo = {
              mainFolderId: mainFolder.folderId,
              mainFolderName: mainFolder.folderName,
              fullPath: mainFolder.folderName,
              folderUrl: `https://lucid.app/documents#/documents?folder_id=${mainFolder.folderId}`
            };

            // Add folder ID to all diagrams
            diagramsData.forEach(diagramData => {
              if (diagramData.metadata) {
                diagramData.metadata.folderId = mainFolder.folderId;
              }
            });
          }
        } catch (folderError) {
          console.warn('LucidExportService: Folder creation failed, continuing without folders:', folderError.message);
        }
      }

      // Split diagrams into document groups based on 2MB size limit
      const documentGroups = this.splitIntoMultiPageDocuments(diagramsData);
      results.documentCount = documentGroups.length;
      results.splitOccurred = documentGroups.length > 1;

      if (results.splitOccurred) {
        console.log(`Document split into ${documentGroups.length} parts due to size constraints`);
      }

      // Process each document group
      for (let groupIndex = 0; groupIndex < documentGroups.length; groupIndex++) {
        const group = documentGroups[groupIndex];
        const partNumber = documentGroups.length > 1 ? groupIndex + 1 : null;

        try {
          // Update progress
          onProgress({
            current: groupIndex + 1,
            total: documentGroups.length,
            flowName: `Document ${groupIndex + 1} of ${documentGroups.length} (${group.pages.length} flows)`,
            status: 'processing'
          });

          // Create multi-page Lucid document
          const lucidDocument = {
            version: 1,
            pages: group.pages
          };

          // Generate document title
          const baseTitle = `${group.metadata.instanceName}-diagrams-${group.metadata.customDate || new Date().toISOString().split('T')[0]}`;
          const documentTitle = partNumber ? `${baseTitle}-part${partNumber}` : baseTitle;

          // Generate .lucid file
          const lucidFile = await this.generateLucidFile(lucidDocument);

          // Download .lucid file locally if requested
          if (downloadLocalCopy) {
            const downloadFilename = partNumber
              ? `${baseTitle}-part${partNumber}.lucid`
              : `${baseTitle}.lucid`;
            this.downloadZipBlob(lucidFile, downloadFilename);
            console.log(`Downloaded local copy: ${downloadFilename}`);
          }

          // Upload to Lucid Charts
          const uploadMetadata = {
            ...group.metadata,
            title: documentTitle,
            flowName: documentTitle,
            pageCount: group.pages.length,
            flowNames: group.flowNames
          };

          const result = await this.uploadToLucid(lucidFile, uploadMetadata);

          results.successful.push({
            documentId: result.id,
            documentUrl: result.url,
            documentName: documentTitle,
            pageCount: group.pages.length,
            flowNames: group.flowNames,
            partNumber: partNumber
          });

          // Update progress
          onProgress({
            current: groupIndex + 1,
            total: documentGroups.length,
            flowName: `Document ${groupIndex + 1} completed`,
            status: 'completed'
          });

        } catch (error) {
          const errorInfo = {
            documentIndex: groupIndex,
            pageCount: group.pages.length,
            error: error.message,
            flowNames: group.flowNames
          };

          results.failed.push(errorInfo);
          onError(errorInfo);

          console.error(`Failed to export document group ${groupIndex + 1}:`, error);

          // Update progress with error
          onProgress({
            current: groupIndex + 1,
            total: documentGroups.length,
            flowName: `Document ${groupIndex + 1} failed`,
            status: 'failed',
            error: error.message
          });
        }
      }

      console.log(`Multi-page export complete: ${results.successful.length} document(s) created, ${results.failed.length} failed`);

      return results;

    } catch (error) {
      console.error('Multi-page document export failed:', error);
      throw new Error(`Multi-page export failed: ${error.message}`);
    }
  }

  /**
   * Create folder structure for organized document storage
   */
  async createFolderStructure(instanceName, viewType, customDate = null) {
    try {
      // Import the folder service dynamically to avoid circular dependencies
      const { lucidFolderService } = await import('./lucidFolderService');
      
      const result = await lucidFolderService.createFolderStructure(instanceName, viewType, customDate);
      
      return result;
    } catch (error) {
      console.error('LucidExportService: Error creating folder structure:', error);
      // Return null to indicate folder creation failed - export can continue without folders
      return null;
    }
  }

  /**
   * Generate .lucid file from Lucid document
   */
  async generateLucidFile(lucidDocument) {
    try {
      // Document validation
      if (!lucidDocument) {
        throw new Error('Lucid document is null or undefined!');
      }

      if (!lucidDocument.pages || !Array.isArray(lucidDocument.pages)) {
        throw new Error('Lucid document missing pages array!');
      }

      if (lucidDocument.pages.length === 0) {
        throw new Error('Lucid document has no pages!');
      }

      const firstPage = lucidDocument.pages[0];
      if (!firstPage.shapes || !Array.isArray(firstPage.shapes)) {
        console.error('CRITICAL ERROR: Page has no shapes array!', firstPage);
        throw new Error('Page missing shapes array!');
      }

      if (firstPage.shapes.length === 0) {
        console.warn('Warning: Page has empty shapes array - this may result in missing diagram content');
        throw new Error('Page has no shapes in diagram');
      }
      
      // Convert document to JSON string
      const documentJson = JSON.stringify(lucidDocument, null, 2);

      // Validate document size
      if (documentJson.length > this.MAX_DOCUMENT_SIZE) {
        throw new Error(`Document too large (${Math.round(documentJson.length / 1024)}KB). Maximum size is ${Math.round(this.MAX_DOCUMENT_SIZE / 1024)}KB.`);
      }

      // Create ZIP file
      const zip = new JSZip();
      
      // Add document.json
      zip.file('document.json', documentJson);

      // Add metadata if present
      if (lucidDocument.metadata) {
        zip.file('metadata.json', JSON.stringify(lucidDocument.metadata, null, 2));
      }

      // Generate ZIP blob with correct MIME type for .lucid files
      const zipArrayBuffer = await zip.generateAsync({
        type: 'arraybuffer',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 6
        }
      });
      
      // Create blob with correct MIME type for .lucid files
      const zipBlob = new Blob([zipArrayBuffer], { 
        type: 'x-application/vnd.lucid.standardImport' 
      });

      // Validate file size
      if (zipBlob.size > this.MAX_FILE_SIZE) {
        throw new Error(`File too large (${Math.round(zipBlob.size / (1024 * 1024))}MB). Maximum size is ${Math.round(this.MAX_FILE_SIZE / (1024 * 1024))}MB.`);
      }


      return zipBlob;

    } catch (error) {
      console.error('Error generating Lucid file:', error);
      throw new Error(`File generation failed: ${error.message}`);
    }
  }

  /**
   * Upload .lucid file to Lucid Charts API
   */
  async uploadToLucid(lucidFile, metadata = {}) {
    try {
      // Get authenticated API client
      const apiClient = await lucidAuthService.createApiClient();

      // Prepare form data
      const formData = new FormData();
      formData.append('file', lucidFile, 'contact-flow.lucid');
      formData.append('type', 'x-application/vnd.lucid.standardImport'); // Required for .lucid file imports
      formData.append('title', this.generateDocumentTitle(metadata));
      formData.append('product', 'lucidchart');

      // Add optional parameters - try different possible field names for folder
      if (metadata.folderId) {
        // Convert folder ID to number as expected by Lucid API
        const folderIdNumber = Number(metadata.folderId);
        formData.append('folderId', folderIdNumber);
        // Also try alternative field names that might be correct
        formData.append('folder', folderIdNumber);
        formData.append('parent', folderIdNumber);
      }

      // Upload to Lucid API
      let response;
      try {
        response = await apiClient.post(this.API_ENDPOINT, formData);
      } catch (uploadError) {
        throw uploadError;
      }

      if (!response || !response.documentId) {
        throw new Error('Invalid response from Lucid API');
      }


      return {
        id: response.documentId,
        documentId: response.documentId,
        title: response.title || this.generateDocumentTitle(metadata),
        url: response.editUrl || response.viewUrl || `https://lucid.app/lucidchart/${response.documentId}/edit`,
        createdAt: new Date().toISOString(),
        ...response
      };

    } catch (error) {
      console.error('Upload to Lucid failed:', error);
      
      // Handle specific API errors
      if (error.message.includes('401') || error.message.includes('unauthorized')) {
        throw new Error('Authentication expired. Please re-authenticate with Lucid Charts.');
      }
      
      if (error.message.includes('403') || error.message.includes('forbidden')) {
        throw new Error('Insufficient permissions. Please check your Lucid Charts account permissions.');
      }
      
      if (error.message.includes('413') || error.message.includes('too large')) {
        throw new Error('File too large for Lucid Charts. Please try with a smaller diagram.');
      }
      
      if (error.message.includes('429') || error.message.includes('rate limit')) {
        throw new Error('Rate limit exceeded. Please wait a moment before trying again.');
      }

      throw new Error(`Upload failed: ${error.message}`);
    }
  }

  /**
   * Generate document title from metadata
   */
  generateDocumentTitle(metadata = {}) {
    if (metadata.flowName) {
      return `Contact Flow: ${metadata.flowName}`;
    }
    
    if (metadata.instanceAlias && metadata.flowType) {
      return `${metadata.instanceAlias} - ${metadata.flowType} Flow`;
    }
    
    if (metadata.instanceAlias) {
      return `${metadata.instanceAlias} - Contact Flow`;
    }
    
    return `Contact Flow - ${new Date().toLocaleDateString()}`;
  }

  /**
   * Generate debug filename for local downloads
   */
  generateDebugFilename(metadata = {}) {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '-');
    
    if (metadata.flowName) {
      // Clean the flow name for use in filename
      const cleanFlowName = metadata.flowName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
      return `ContactFlow-${cleanFlowName}-${timestamp}.lucid`;
    }
    
    if (metadata.instanceAlias) {
      const cleanInstanceAlias = metadata.instanceAlias.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
      return `ContactFlow-${cleanInstanceAlias}-${timestamp}.lucid`;
    }
    
    return `ContactFlow-DEBUG-${timestamp}.lucid`;
  }

  /**
   * Download .lucid file locally (for debugging/backup)
   */
  async downloadLucidFile(lucidDocument, filename = 'contact-flow.lucid') {
    try {
      const lucidFile = await this.generateLucidFile(lucidDocument);
      
      // Create download link
      const url = URL.createObjectURL(lucidFile);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      
      // Clean up
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Download failed:', error);
      throw new Error(`Download failed: ${error.message}`);
    }
  }

  /**
   * Download ZIP blob locally (helper method for debugging)
   */
  downloadZipBlob(zipBlob, filename = 'contact-flow.lucid') {
    try {
      // Create download link
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      
      // Clean up
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('ZIP blob download failed:', error);
      throw new Error(`ZIP blob download failed: ${error.message}`);
    }
  }

  /**
   * Validate export requirements
   */
  validateExportRequirements() {
    const issues = [];

    // Check authentication
    if (!lucidAuthService.isAuthenticated()) {
      issues.push('Not authenticated with Lucid Charts');
    }

    // Check user info
    const userInfo = lucidAuthService.getUserInfo();
    if (!userInfo) {
      issues.push('User information not available');
    }

    // Check browser capabilities
    if (!window.File || !window.FileReader || !window.Blob) {
      issues.push('Browser does not support required file operations');
    }

    // Check JSZip availability
    if (!JSZip) {
      issues.push('ZIP library not available');
    }

    return {
      isValid: issues.length === 0,
      issues: issues,
      userInfo: userInfo
    };
  }

  /**
   * Get export capabilities and limits
   */
  getExportCapabilities() {
    const validation = this.validateExportRequirements();
    
    return {
      isAvailable: validation.isValid,
      maxFileSize: this.MAX_FILE_SIZE,
      maxDocumentSize: this.MAX_DOCUMENT_SIZE,
      maxFileSizeMB: Math.round(this.MAX_FILE_SIZE / (1024 * 1024)),
      maxDocumentSizeMB: Math.round(this.MAX_DOCUMENT_SIZE / (1024 * 1024)),
      supportedFormats: ['lucidchart'],
      batchExportSupported: true,
      authentication: {
        isAuthenticated: lucidAuthService.isAuthenticated(),
        userInfo: lucidAuthService.getUserInfo()
      },
      issues: validation.issues
    };
  }

  /**
   * Estimate export time based on diagram complexity
   */
  estimateExportTime(diagramsData) {
    if (!Array.isArray(diagramsData)) {
      diagramsData = [diagramsData];
    }

    let totalComplexity = 0;

    diagramsData.forEach(diagram => {
      const nodes = diagram.reactFlowData?.nodes?.length || 0;
      const edges = diagram.reactFlowData?.edges?.length || 0;
      // Rough complexity estimate
      totalComplexity += (nodes * 2) + (edges * 1);
    });

    // Base time + complexity factor + API overhead + folder creation
    const baseTime = 2000; // 2 seconds base
    const complexityTime = totalComplexity * 100; // 100ms per complexity point
    const apiTime = diagramsData.length * 1000; // 1 second per diagram for API calls
    const folderTime = 2000; // 2 seconds for folder creation

    const estimatedMs = baseTime + complexityTime + apiTime + folderTime;

    return {
      estimatedTimeMs: estimatedMs,
      estimatedTimeSeconds: Math.round(estimatedMs / 1000),
      complexity: totalComplexity,
      diagramCount: diagramsData.length
    };
  }

  /**
   * Calculate the size of a Lucid document when serialized to JSON
   * @param {Object} document - Lucid document object
   * @returns {number} Size in bytes
   */
  calculateDocumentSize(document) {
    try {
      const documentJson = JSON.stringify(document, null, 2);
      return documentJson.length;
    } catch (error) {
      console.error('Error calculating document size:', error);
      return 0;
    }
  }

  /**
   * Split diagrams into multiple documents to stay within size limits
   * @param {Array} diagramsData - Array of {lucidData, metadata} objects
   * @param {number} maxSizeBytes - Maximum size per document (default: 90% of MAX_DOCUMENT_SIZE)
   * @returns {Array} Array of document groups with pages and metadata
   */
  splitIntoMultiPageDocuments(diagramsData, maxSizeBytes = null) {
    const MAX_SIZE = maxSizeBytes || (this.MAX_DOCUMENT_SIZE * 0.9); // 90% safety margin
    const documentGroups = [];
    let currentGroup = {
      pages: [],
      flowNames: [],
      metadata: null // Will be set from first diagram in group
    };

    for (let i = 0; i < diagramsData.length; i++) {
      const diagramData = diagramsData[i];

      // Skip failed transformations
      if (!diagramData.lucidData || diagramData.metadata?.transformationFailed) {
        console.warn(`Skipping failed transformation: ${diagramData.metadata?.flowName || 'Unknown'}`);
        continue;
      }

      // Extract the page from the lucidData (each diagram has 1 page)
      const page = diagramData.lucidData.pages[0];
      if (!page) {
        console.warn(`No page found in diagram: ${diagramData.metadata?.flowName || 'Unknown'}`);
        continue;
      }

      // Set page title to contact flow name
      page.title = diagramData.metadata?.flowName || `Contact Flow ${i + 1}`;

      // Ensure unique page ID
      page.id = `page_${i}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Make all IDs within the page unique to prevent collisions (CRITICAL FIX)
      const pagePrefix = `p${i}`;
      this.makePageIdsUnique(page, pagePrefix);

      // Create test document with this page added
      const testDocument = {
        version: 1,
        pages: [...currentGroup.pages, page]
      };

      const testSize = this.calculateDocumentSize(testDocument);

      // Check if adding this page would exceed the limit
      if (testSize > MAX_SIZE && currentGroup.pages.length > 0) {
        // Save current group and start new one
        documentGroups.push({ ...currentGroup });
        currentGroup = {
          pages: [page],
          flowNames: [page.title],
          metadata: diagramData.metadata
        };
      } else {
        // Add page to current group
        currentGroup.pages.push(page);
        currentGroup.flowNames.push(page.title);
        if (!currentGroup.metadata) {
          currentGroup.metadata = diagramData.metadata;
        }
      }
    }

    // Add final group if it has pages
    if (currentGroup.pages.length > 0) {
      documentGroups.push(currentGroup);
    }

    console.log(`Split ${diagramsData.length} diagrams into ${documentGroups.length} document(s)`);
    documentGroups.forEach((group, index) => {
      const size = this.calculateDocumentSize({ version: 1, pages: group.pages });
      console.log(`  Document ${index + 1}: ${group.pages.length} pages, ${Math.round(size / 1024)}KB`);
    });

    return documentGroups;
  }

  /**
   * Make all IDs within a page globally unique by adding a prefix
   * This prevents ID collisions when combining multiple pages into one document
   * @param {Object} page - Page object with shapes, lines, groups, layers
   * @param {string} pagePrefix - Unique prefix for this page (e.g., "p0", "p1")
   * @returns {Object} Page with all IDs prefixed and references updated
   */
  makePageIdsUnique(page, pagePrefix) {
    const idMap = new Map(); // Old ID -> New ID mapping

    // 1. Update shape IDs and build mapping
    if (page.shapes && Array.isArray(page.shapes)) {
      page.shapes.forEach(shape => {
        if (shape.id) {
          const oldId = shape.id;
          const newId = `${pagePrefix}_${oldId}`;
          idMap.set(oldId, newId);
          shape.id = newId;
        }
      });
    }

    // 2. Update line IDs and build mapping
    if (page.lines && Array.isArray(page.lines)) {
      page.lines.forEach(line => {
        if (line.id) {
          const oldId = line.id;
          const newId = `${pagePrefix}_${oldId}`;
          idMap.set(oldId, newId);
          line.id = newId;
        }
      });
    }

    // 3. Update group IDs if present
    if (page.groups && Array.isArray(page.groups)) {
      page.groups.forEach(group => {
        if (group.id) {
          const oldId = group.id;
          const newId = `${pagePrefix}_${oldId}`;
          idMap.set(oldId, newId);
          group.id = newId;
        }
      });
    }

    // 4. Update layer IDs if present
    if (page.layers && Array.isArray(page.layers)) {
      page.layers.forEach(layer => {
        if (layer.id) {
          const oldId = layer.id;
          const newId = `${pagePrefix}_${oldId}`;
          idMap.set(oldId, newId);
          layer.id = newId;
        }
      });
    }

    // 5. Update ALL references to IDs

    // 5a. Update line endpoint references (CRITICAL - fixes cross-page reference errors)
    if (page.lines && Array.isArray(page.lines)) {
      page.lines.forEach(line => {
        if (line.endpoint1 && line.endpoint1.shapeId) {
          const oldShapeId = line.endpoint1.shapeId;
          if (idMap.has(oldShapeId)) {
            line.endpoint1.shapeId = idMap.get(oldShapeId);
          }
        }
        if (line.endpoint2 && line.endpoint2.shapeId) {
          const oldShapeId = line.endpoint2.shapeId;
          if (idMap.has(oldShapeId)) {
            line.endpoint2.shapeId = idMap.get(oldShapeId);
          }
        }
      });
    }

    // 5b. Update layer items references
    if (page.layers && Array.isArray(page.layers)) {
      page.layers.forEach(layer => {
        if (layer.items && Array.isArray(layer.items)) {
          layer.items = layer.items.map(itemId => {
            return idMap.has(itemId) ? idMap.get(itemId) : itemId;
          });
        }
      });
    }

    // 5c. Update group items references if present
    if (page.groups && Array.isArray(page.groups)) {
      page.groups.forEach(group => {
        if (group.items && Array.isArray(group.items)) {
          group.items = group.items.map(itemId => {
            return idMap.has(itemId) ? idMap.get(itemId) : itemId;
          });
        }
      });
    }

    return page;
  }
}

// Export singleton instance
export const lucidExportService = new LucidExportService();
export default lucidExportService;